import type { SxProps } from "@mui/system";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
} from "@vapor/react-material";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";
import { useUnlinkTaxPayersFromFulfilment } from "../../../../../core/api/use-unlink-tax-payers-from-fulfilment/useUnlinkTaxPayersFromFulfilment";
import { useSuccessNotification } from "../../../../../core/hooks/useSuccessNotification";

interface FulfilmentUnlinkAllTaxPayersProps {
    children?: string | JSX.Element | JSX.Element[];
    levyId: string;
    year: number;
    onCancel?: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
    onUnlinkAll?: (event: React.MouseEvent<HTMLButtonElement>) => unknown;
    sx?: SxProps;
}

export const FulfilmentUnlinkAllTaxPayers = ({
    levyId,
    year,
    onCancel,
    onUnlinkAll,
    sx,
}: FulfilmentUnlinkAllTaxPayersProps): ReactElement => {
    const { t } = useTranslation();
    const { showTextMessage } = useSuccessNotification();

    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const unlinkTaxPayersMutation = useUnlinkTaxPayersFromFulfilment();

    const onClickCancelDialog = (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        setIsDialogOpen(false);
        if (onCancel) onCancel(event);
    };

    const onClickConfirmDialog = async (
        event: React.MouseEvent<HTMLButtonElement>,
    ) => {
        await unlinkTaxPayersMutation.mutateAsync({
            levyId: levyId,
            year: year,
        });

        showTextMessage(
            t("components.unlink_all_fulfilments.notification.success"),
        );

        setIsDialogOpen(false);
        if (onUnlinkAll) onUnlinkAll(event);
    };

    return (
        <>
            <Dialog open={isDialogOpen} onClose={onClickCancelDialog}>
                <DialogTitle>
                    {t("components.unlink_all_fulfilments.dialog.title")}
                </DialogTitle>
                <Divider variant="fullWidth" />
                <DialogContent>
                    <DialogContentText>
                        {t("components.unlink_all_fulfilments.dialog.content")}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        disabled={unlinkTaxPayersMutation.isPending}
                        onClick={onClickCancelDialog}
                        variant="outlined"
                    >
                        {t(
                            "components.unlink_all_fulfilments.dialog.actions.cancel",
                        )}
                    </Button>
                    <Button
                        disabled={unlinkTaxPayersMutation.isPending}
                        color="error"
                        onClick={onClickConfirmDialog}
                        variant="contained"
                    >
                        {t(
                            "components.unlink_all_fulfilments.dialog.actions.confirm",
                        )}
                        {unlinkTaxPayersMutation.isPending && (
                            <CircularProgress
                                color="inherit"
                                sx={{ ml: 1 }}
                                size={16}
                            />
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
            <Box onClick={() => setIsDialogOpen(true)} sx={sx}>
                {t("views.home.components.fulfilment.menu_items.unlink")}
            </Box>
        </>
    );
};
